import $ from 'jQuery';
// @ts-ignore
import { transformToTitleCase } from '../../../helpers';
import './base_components.css';
import { debounce } from 'lodash';
import helpers from '../../../helpers';
import widgets from '../../widgets';
import environment from "../../../environment";

/**
 *
 * @param {string} pName - Title of the autocomplete input widget
 * @param {string} pTooltip - Tooltip that is shown on hover
 * @param {string} pInitialValue - Initial value to populate the widget with
 * @param {Array<String>} pAutoCompleteOptions - List of autocomplete options
 * @param {Function} pOnValue - Function to call on submit
 * @param {Array<Function>} pValidationFunctions - Validation functions to test the input against
 * @param {{labelIcon: JQuery<HTMLElement>, getUnit: Function}} [pOptions] - Additional flags for the widget ( right now only supports adding a optional unit to the end of the widget)
 * @returns {{ widget: JQuery<HTMLElement>, disableInput: Function, enableInput: Function, updateUnit: Function, updateValue: Function }}
 */
const autocompleteComponent = (pName, pTooltip, pInitialValue, pAutoCompleteOptions, pOnValue, pValidationFunctions, pOptions) => {
  // pName, pTooltip, pUnits, pOptions, pInitialSource, pValidationFunctions, pValue, pOnValue
  const lValidationFunction = helpers.validateForm(pValidationFunctions);
  const lWidget = baseComponent(pName, pOptions?.labelIcon).widget;

  // Add tooltip
  lWidget.attr({ 'title': pTooltip });

  const lInputContainer = $('<div>').addClass('ml_autocomplete_component');

  // probably cause by box-sizing difference between/cad (maybe, could not isolate)
  if(environment.isOnCad())  { lInputContainer.css('height', '2.5rem'); }

  const lInput = $('<input>');

  // add autocomplete functionality
  _applyAutoCompleteWithOptions(lInput, pAutoCompleteOptions);

  // set initialValue
  lInput.val(pInitialValue);
  let lLastValidValue = pInitialValue;

  const lErrorMessage = $('<span>').addClass('ml_autocomplete_input_validation_error');

  function showError(message) {
    lErrorMessage.text(message).show();
    lInputContainer.addClass('error');
  }

  function hideError() {
    lErrorMessage.hide();
    lInputContainer.removeClass('error');
  }

  // Save on change
  lInput.on('change', (pEvent) => {
    pEvent.stopPropagation();
    const lNewValue = pEvent.target.value.trim();
    const lValidationError = lValidationFunction(lNewValue);
    if (lValidationError !== undefined) {
      lInput.val(lLastValidValue);
      widgets.messageBox('Your changes have been reverted.', 'warning');
    }
    else {
      pOnValue(lNewValue);
      lLastValidValue = lNewValue;
    }
    hideError();
  }) ;

  // Validate on input
  lInput.on('input', debounce(function (pEvent) {
    pEvent.stopPropagation();
    const lNewValue = pEvent.target.value.trim();
    const lValidationError = lValidationFunction(lNewValue);
    lValidationError ? showError(lValidationError) : hideError();
  }, 300));

  let lUnitContainer;
  if (pOptions?.getUnit) {
    lUnitContainer = $('<div>').addClass('ml_autocomplete_unit').html(pOptions.getUnit());
    lInput.addClass('ml_autocomplete_input_with_units');
  }

  lWidget.append(
    lInputContainer.append(lInput, lUnitContainer),
    lErrorMessage
  );

  return {
    widget: lWidget,
    disableInput: () => { lInput.attr('disabled', true); },
    enableInput: () => { lInput.removeAttr('disabled'); },
    updateUnit: (pUnit) => {
      if (pUnit) { lUnitContainer.html(pUnit); }
      else if (pOptions?.getUnit) { lUnitContainer.html(pOptions.getUnit()); }
    },
    updateValue: (pValue) => { lInput.val(pValue); }
  };
};

const _applyAutoCompleteWithOptions = (pInput, pAutoCompleteOptions) => {
  let lCache = pInput.val();
  let lCursorPos = 0;

  pInput.autocomplete({
    source: function (request, response) {
      if (getVariableAt(lCache, lCursorPos).variable.length == 0) { response([]); }
      else { response($.ui.autocomplete.filter(pAutoCompleteOptions, getVariableAt(lCache, lCursorPos).variable)); }
    },
    focus: function () {
      // prevent value inserted on focus
      return false;
    },
  });

  pInput.on('input', function (e) {
    lCache = $(this).val();
    lCursorPos = e.target.selectionStart;
  });

  // select event
  pInput.on('autocompleteselect', function (pEvent, pUI) {
    pEvent.stopPropagation();

    const lVariableAtPos = getVariableAt(lCache, lCursorPos);
    if (lCursorPos == lCache.length) {
      if (lVariableAtPos.variable.length != 0) { lCache = lCache.slice(0, -1 * lVariableAtPos.variable.length); }
      lCache = lCache + pUI.item.value;
    }
    else {
      lCache = `${lCache.slice(0, lVariableAtPos.left)} ${pUI.item.value} ${lCache.slice(lVariableAtPos.right + lCursorPos)}`;
    }

    pInput.val(lCache);

    // $.val(newValue) doesn't trigger input events. The VirtualKeyboard needs to know that the input has changed.
    pInput[0].dispatchEvent(new Event('input', { bubbles: true }));

    return false;
  });

  function getVariableAt(str, pos) {
    // Search for the word's beginning and end.
    let lLeftPos;
    // eslint-disable-next-line no-useless-escape
    const lRegex = /[\+\-\,\*\/]/g;
    const lMatches = [...str.slice(0, pos + 1).matchAll(lRegex)];
    if (lMatches.length == 0) { lLeftPos = 0; }
    else { lLeftPos = lMatches[lMatches.length - 1].index + 1; }
    const lRightPos = str.slice(pos).search(lRegex);

    // The last word in the string is a special case.
    if (lRightPos < 0) {
      return { variable: str.slice(lLeftPos).trim(), left: lLeftPos, right: lRightPos };
    }
    // Return the word, using the located bounds to extract it from the string.
    return { variable: str.slice(lLeftPos, lRightPos + pos).trim(), left: lLeftPos, right: lRightPos };
  }

  // get rid of default styles
  pInput.autocomplete('instance')._renderMenu = function (ul, items) {
    const lThis = this;
    $.each(items, function (index, item) { lThis._renderItemData(ul, item); });
  };

  pInput.autocomplete('instance')._renderItem = function (ul, item) {
    return $("<li>").append(item.label).appendTo(ul);
  };
};

/**
 * A base widget component that must be used by other widget to preserve the ml theme.
 * @param {string} [pLabelText] The label of the widget
 * @param {JQuery<HTMLElement>} [pLabelIcon] label icon of the widget
 * @returns {{ widget: JQuery<HTMLElement> }}
 */
const baseComponent = (pLabelText, pLabelIcon) => {
  const lWidget = $('<div>').addClass('v_layout spaced');
  if (pLabelText) {
    const lLabel = $('<h5>').addClass('ml_component_label').text(pLabelText);
    if (pLabelIcon) {
      pLabelIcon.css({ marginLeft: '10px' });
      lWidget.append(
        $('<div>').css({ display: 'flex' }).append(lLabel, pLabelIcon)
      );
    }
    else {
      lWidget.append(lLabel);
    }
  }
  return { widget: lWidget };
};

export {
  autocompleteComponent
};
